/*  MUI  */
import { Box, CircularProgress, Grid, Tab } from '@mui/material';

/*  Shared  */
import { PageTitle } from 'application/Shared/Components/App';
import { AdminBackButton } from 'application/Shared/Components/Buttons/BackButton';
import { IdentityManagementIntegrations } from 'application/Shared/Components/Client/IdentityManagementIntegrations';
import { MUITabsWrapper } from 'application/Shared/Components/MUITabsWrapper';
import { Notify, Response, Route } from 'application/Shared/Helpers';
import { Requests } from 'application/Shared/Requests';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  React  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { withRouter } from 'router/withRouter';

/*  Components  */
import { AdminDeploymentScripts } from './internal/AdminDeploymentScripts';

class ProviderIntegration extends Component {
    constructor(props) {
        super(props);

        this.state = {
            error: false,
            loading: true,
            provider: null,
            tenant: null,
            token: null
        };

        /* requests */
        this.requests = new Requests();
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.requests.cancel();
    }

    load = () => {
        this.loadSettings();
    };

    loadSettings = async () => {
        const { match, t } = this.props;
        const { partnerId, clientId } = match.params;

        try {
            const providerParams = {
                includeConnectionTestDetails: true,
                platformTenantId: clientId,
                size: -1
            };
            const tenantParams = {
                id: clientId,
                includeSettings: true,
                size: 1
            };

            const tenantTokenParams = {
                platformTenantId: clientId,
                size: 1
            };

            // If there's a partnerId, then this is being viewed by a partner user and we need to add includeClients
            // to ensure client data is loaded.
            if (partnerId) {
                providerParams.includeClients = true;
                tenantParams.includeClients = true;
                tenantTokenParams.includeClients = true;
            }

            const providerResponse = await axios.get('/tenantProvider', {
                cancelToken: this.requests.token(),
                params: providerParams
            });

            const tenantResponse = await axios.get('/tenant', {
                cancelToken: this.requests.token(),
                params: tenantParams
            });

            const tenantTokenResponse = await axios.get('/tenantToken', {
                cancelToken: this.requests.token(),
                params: tenantTokenParams
            });

            if (!Response.hasData(tenantResponse)) {
                this.setState({
                    error: true,
                    loading: false
                });

                Notify.error(t('Clients.integrations.pageLoadError'));
                return;
            }

            const provider = Response.getData(providerResponse);
            const tenant = Response.getOne(tenantResponse);
            const tenantToken = Response.getOne(tenantTokenResponse);

            const { token } = tenantToken;

            if (tenant?.settings?.cw_clientId) {
                tenant.clientId = tenant.settings.cw_clientId;
            }

            this.setState({
                loading: false,
                provider,
                tenant,
                token
            });
        } catch (e) {
            debug.e('Error Loading Settings', e);
        }
    };

    reload = () => {
        this.setState({ loading: true });
        this.load();
    };

    render() {
        const { match, t } = this.props;

        const { error, loading, provider, tenant, token } = this.state;

        const { partnerId, clientId } = match.params;

        const baseAdminRoute = Route.buildBaseAdminRoute({
            clientId,
            partnerId
        });

        const { tab } = match.params;
        const activeTab = tab ?? 'configuration';

        const tabs = [];
        tabs.push(
            <Tab
                component={Link}
                to={`${baseAdminRoute}/identityManagement/configuration`}
                value="configuration"
                label={t('Common.configuration')}
                aria-label={t(
                    'Provider.MicrosoftConfiguration.configuration_title'
                )}
                key="configuration"
                data-trackid="admin_integrations_configuration"
            />
        );
        tabs.push(
            <Tab
                component={Link}
                to={`${baseAdminRoute}/identityManagement/deploymentScripts`}
                key="deployment_tab_deployment_scripts"
                value="deploymentScripts"
                label={t('Common.deployment_scripts')}
                aria-label={t('Deployment_Page.deployment_scripts')}
                data-trackid="admin_integrations_deploymentScript"
            />
        );

        if (loading) {
            return (
                <Box display="flex" justifyContent="center" width="100%">
                    <CircularProgress />
                </Box>
            );
        }

        if (error) {
            return (
                <>
                    <PageTitle title={t('Common.configuration')} />
                    <AdminBackButton linkTo="/configure" />
                </>
            );
        }

        return (
            <>
                <PageTitle title={t('Common.configuration')} />
                <AdminBackButton linkTo="/configure" />
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <MUITabsWrapper value={activeTab}>
                            {tabs}
                        </MUITabsWrapper>
                    </Grid>
                </Grid>
                {activeTab === 'deploymentScripts' && (
                    <AdminDeploymentScripts token={token} />
                )}
                {activeTab === 'configuration' && (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <IdentityManagementIntegrations
                                clientTenant={tenant}
                                providers={provider}
                                isCollapsible={false}
                                refetchClientData={() => this.reload()}
                            />
                        </Grid>
                    </Grid>
                )}
            </>
        );
    }
}
export default withRouter(withTranslation()(ProviderIntegration));
